import React, { useEffect, useState } from 'react';

import InfoBanner from './../segments/InfoBanner';
import SchemaProducts from './../segments/schemaProducts';
import Loader from './../segments/loader';
import { Button } from '../../commons/utils';
import Planogram from '../segments/planogram';
import DevicesByLocations from '../segments/devicesByLocations';
import { isScaleCabinet, isScale2Cabinet, getCabinetShelfConfig, getCabinetSettings } from '../utils';
import { keycloakRef } from '../../auth/KeycloakProvider';

const RefillSchemas = ({
    cabinets,
    locations,
    refillRule,
    fetchCabinetRefillRule,
    allProducts,
    updateCabinetRefillSchema,
    createRefillSchema,
    deleteRefillSchema,
    fetchRefillRules,
    t,
    postSettingsCopyRequest,
    allRefillRules,
    allRefillPlans,
    uploadPlanogramImage,
    planogramImageInStore,
    isAdmin,
    planogram,
    fetchPlanogram,
    savePlanogram,
    deletePlanogram,
}) => {
    const [selectedCabinet, setSelectedCabinet] = useState();
    const [createNew, setCreateNew] = useState(false);
    const [activeViewMode, setActiveViewMode] = useState('inventory');
    const [schemaPlanogramData, setSchemaPlanogramData] = useState({});
    const [shelfConfigError, setShelfConfigError] = useState(false);

    useEffect(() => {
        if (!selectedCabinet) return;

        fetchCabinetRefillRule(selectedCabinet.deviceCode);
        fetchPlanogram(selectedCabinet.deviceCode);
        setCreateNew(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCabinet]);

    useEffect(() => {
        const { data, isDeleted } = planogram;
        if (!data) {
            // a deleted planogram must not linger on screen; while a fetch is in flight keep what is shown
            if (isDeleted) setSchemaPlanogramData({});
            return;
        }

        setSchemaPlanogramData(data);
    }, [planogram, activeViewMode]);

    /**
     * inital planogram shelves configuration of the cabinet based on the cabinet subtype
     * @returns
     */
    const initPlanogramConfig = async () => {
        if (!selectedCabinet) return;
        let planogramConfig;

        // scale2: the planner creates the shelves and slots first (type scale2, no laneId yet); when the
        // weight-service lanes are installed the cabinet merges its shelfId/laneId onto them by position
        if (isScale2Cabinet(selectedCabinet)) {
            const emptySlots = () => [1, 2, 3, 4, 5].map(slot => ({ slot, products: [] }));
            setSchemaPlanogramData({
                shelvesCount: 5,
                shelves: [1, 2, 3, 4, 5].map(shelf => ({ shelf, type: 'scale2', slots: emptySlots() })),
            });
            return;
        }

        if (!isScaleCabinet(selectedCabinet)) {
            planogramConfig = {
                shelvesCount: 5,
                shelves: [
                    {
                        shelf: 1,
                        type: 'rfid',
                        slots: [
                            { slot: 1, products: [] },
                            { slot: 2, products: [] },
                            { slot: 3, products: [] },
                            { slot: 4, products: [] },
                            { slot: 5, products: [] },
                        ],
                    },
                    {
                        shelf: 2,
                        type: 'rfid',
                        slots: [
                            { slot: 1, products: [] },
                            { slot: 2, products: [] },
                            { slot: 3, products: [] },
                            { slot: 4, products: [] },
                            { slot: 5, products: [] },
                        ],
                    },
                    {
                        shelf: 3,
                        type: 'rfid',
                        slots: [
                            { slot: 1, products: [] },
                            { slot: 2, products: [] },
                            { slot: 3, products: [] },
                            { slot: 4, products: [] },
                            { slot: 5, products: [] },
                        ],
                    },
                    {
                        shelf: 4,
                        type: 'rfid',
                        slots: [
                            { slot: 1, products: [] },
                            { slot: 2, products: [] },
                            { slot: 3, products: [] },
                            { slot: 4, products: [] },
                            { slot: 5, products: [] },
                        ],
                    },
                    {
                        shelf: 5,
                        type: 'rfid',
                        slots: [
                            { slot: 1, products: [] },
                            { slot: 2, products: [] },
                            { slot: 3, products: [] },
                            { slot: 4, products: [] },
                            { slot: 5, products: [] },
                        ],
                    },
                ],
            };
        } else {
            const config = await getCabinetShelfConfig();
            const selectedCabinetSetting = await getCabinetSettings(selectedCabinet.deviceCode, keycloakRef, config);
            if ('shelvesCount' in selectedCabinetSetting) {
                planogramConfig = selectedCabinetSetting;
            } else {
                setShelfConfigError(true);
            }
        }

        setSchemaPlanogramData(planogramConfig);
    };

    return (
        <div className="refillschema">
            <DevicesByLocations
                cabinets={cabinets}
                locations={locations}
                t={t}
                cabinetSelectionChange={cabinet => {
                    setSelectedCabinet(cabinet);
                    // scale cabinets (scale1 and scale2) open on the planogram view, the slot layout is what
                    // matters for them; rfid cabinets keep opening on the inventory view as before
                    setActiveViewMode(isScaleCabinet(cabinet) || isScale2Cabinet(cabinet) ? 'planogram' : 'inventory');
                }}
            />
            <div className="refillschema__contents">
                {(() => {
                    if (!selectedCabinet) return <InfoBanner message={t('refillSchemaSubText')} />;

                    if (refillRule.fetching) return <Loader message={t('main:fetchingLoader')} />;

                    if (!createNew && (!refillRule.rule || !Object.keys(refillRule.rule).length)) {
                        return (
                            <div className="refillschema__contents-no-rule">
                                <InfoBanner message={t('noRefillSchema')} />
                                <Button
                                    type="add"
                                    text={t('createRefillSchema')}
                                    onClick={() => {
                                        setCreateNew(true);
                                        setActiveViewMode('inventory');
                                    }}
                                />
                            </div>
                        );
                    }

                    if (activeViewMode === 'planogram') {
                        //if no planogram data, suggest to create a new one.
                        if (!schemaPlanogramData || !Object.keys(schemaPlanogramData).length) {
                            return (
                                <div>
                                    <div className="refillschema__controller">
                                        <div className="refillschema__controller-toggle">
                                            <span
                                                className={`${
                                                    activeViewMode === 'inventory'
                                                        ? 'refillschema__controller-toggle-inventory refillschema__controller-toggle--active'
                                                        : ''
                                                }`}
                                                onClick={() => setActiveViewMode('inventory')}
                                            >
                                                {t('inventory')}
                                            </span>
                                            <span
                                                className={`${
                                                    activeViewMode === 'planogram'
                                                        ? 'refillschema__controller-toggle-planogram refillschema__controller-toggle--active'
                                                        : ''
                                                }`}
                                                onClick={() => setActiveViewMode('planogram')}
                                            >
                                                {t('planogram')}
                                            </span>
                                        </div>
                                    </div>
                                    {}
                                    <InfoBanner message={Object.keys(refillRule.rule).length ? t('noPlanogramInSchema') : t('createSchemaFirst')} />
                                    {shelfConfigError && <div className="refillschema__shelfconfig-error">{t('shelfConfigError')}</div>}
                                    {isAdmin && Object.keys(refillRule.rule).length > 0 && (
                                        <Button type="add" text={t('createRefillPlanogram')} mt onClick={() => initPlanogramConfig()} />
                                    )}
                                </div>
                            );
                        } else {
                            return (
                                <Planogram
                                    toggleViewMode={mode => setActiveViewMode(mode)}
                                    activeViewMode={activeViewMode}
                                    t={t}
                                    cabinetRefillSchema={refillRule}
                                    allProducts={allProducts}
                                    updateCabinetRefillSchema={updateCabinetRefillSchema}
                                    selectedCabinet={selectedCabinet}
                                    fetchRefillRules={fetchRefillRules}
                                    planogramProductData={schemaPlanogramData}
                                    planogramImageInStore={planogramImageInStore}
                                    uploadPlanogramImage={uploadPlanogramImage}
                                    isAdmin={isAdmin}
                                    savePlanogram={savePlanogram}
                                    planogramStore={planogram}
                                    fetchPlanogram={fetchPlanogram}
                                    deletePlanogram={deletePlanogram}
                                />
                            );
                        }
                    }

                    return (
                        <SchemaProducts
                            selectedCabinet={selectedCabinet}
                            cabinetRefillSchema={refillRule}
                            allProducts={allProducts}
                            updateCabinetRefillSchema={updateCabinetRefillSchema}
                            createRefillSchema={createRefillSchema}
                            deleteRefillSchema={deleteRefillSchema}
                            fetchRefillRules={fetchRefillRules}
                            fetchCabinetRefillRule={fetchCabinetRefillRule}
                            t={t}
                            toggleViewMode={mode => {
                                setActiveViewMode(mode);
                            }}
                            activeViewMode={activeViewMode}
                            locations={locations}
                            cabinets={cabinets}
                            copyRefillSchemaToOtherCabinets={postSettingsCopyRequest}
                            allRefillSchemas={allRefillRules}
                            allRefillPlans={allRefillPlans}
                            isAdmin={isAdmin}
                        />
                    );
                })()}
            </div>
        </div>
    );
};

export default RefillSchemas;
